import { useMemo } from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { normalizeSearchParams, type SearchCoursesQuery } from '@admitly/shared';
import { Route as appRoute } from '../app.js';
import { useCourseSearch } from '../../hooks/useCourseSearch.js';
import { useShortlistIds, useToggleShortlist } from '../../hooks/useShortlist.js';
import { SearchBox } from '../../components/search/SearchBox.js';
import { FilterChips } from '../../components/search/FilterChips.js';
import { CourseCard } from '../../components/courses/CourseCard.js';
import { Button } from '../../components/ui/Button.js';
import { pickRawSearch, type PatchFn, type RawSearch } from '../../components/search/types.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/search',
  validateSearch: (raw: Record<string, unknown>): RawSearch => pickRawSearch(raw),
  component: SearchPage,
});

function SearchPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const onPatch: PatchFn = (patch) => {
    void navigate({
      to: '/app/search',
      search: (prev: RawSearch) => {
        const next: RawSearch = { ...prev, ...patch };
        for (const k of Object.keys(next) as (keyof RawSearch)[]) {
          if (next[k] === undefined) delete next[k];
        }
        return next;
      },
    });
  };

  const clearAll = () => void navigate({ to: '/app/search', search: {} });

  // Reuse the exact wire-format parser the API uses; junk URL params fall back
  // to an empty query rather than crashing the page.
  const query = useMemo<SearchCoursesQuery>(() => {
    try {
      return normalizeSearchParams(search as Record<string, unknown>);
    } catch {
      return {};
    }
  }, [search]);

  const { data, isLoading, error } = useCourseSearch(query);
  const hasFilters = Object.keys(search).length > 0;

  const shortlistIds = useShortlistIds();
  const toggleShortlist = useToggleShortlist();

  return (
    <div className="max-w-6xl">
      <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">
        Catalogue
      </div>
      <h1 className="font-serif text-4xl tracking-tighter2 mb-4">Find courses</h1>

      <div className="space-y-4 mb-6">
        <SearchBox value={search.q ?? ''} onPatch={onPatch} />
        <FilterChips search={search} onPatch={onPatch} />
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between">
          <p className="text-muted text-sm">
            {isLoading
              ? 'Searching…'
              : `${data?.count ?? 0} result${(data?.count ?? 0) === 1 ? '' : 's'}`}
          </p>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              Clear all
            </Button>
          )}
        </div>

        {!isLoading && data && typeof data.hiddenNoFee === 'number' && data.hiddenNoFee > 0 && (
          <p className="text-muted text-xs mt-1.5">
            {data.hiddenNoFee === 1
              ? '1 more course matches but has no annual fee recorded, so the fee filter hides it.'
              : `${data.hiddenNoFee} more courses match but have no annual fee recorded, so the fee filter hides them.`}
          </p>
        )}
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="surface p-5 animate-pulse">
              <div className="h-4 w-20 bg-cream-deep rounded mb-4" />
              <div className="h-6 w-3/4 bg-cream-deep rounded mb-2" />
              <div className="h-3 w-1/2 bg-cream-deep rounded mb-6" />
              <div className="h-10 w-full bg-cream-deep rounded" />
            </div>
          ))}
        </div>
      )}

      {error && <div className="surface p-5 text-danger text-sm">Couldn't run the search.</div>}

      {!isLoading && data && data.items.length === 0 && (
        <div className="surface-soft p-8 text-center">
          <h3 className="font-serif text-lg text-ink mb-1">No matching courses</h3>
          <p className="text-sm text-muted max-w-md mx-auto">
            Try a different search term or relax the filters.
          </p>
        </div>
      )}

      {!isLoading && data && data.items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((c) => {
            const saved = shortlistIds.has(c.id);
            return (
              <CourseCard
                key={c.id}
                course={c}
                isStarred={saved}
                onToggleStar={(courseId) => toggleShortlist.mutate({ courseId, saved })}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
